import { Table } from "antd";
import { IoMdRefresh } from "react-icons/io";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import LogicContext from "../../Context/LogicContext";
import GeneralCustomTable from "../../helpers/GeneralCustomTable";
import { mobileAppService } from "../../services/mobileApp.service";

const DAYS_OF_MONTH = Array.from({ length: 31 }, (_, index) => index + 1);
const MONTH_ORDER = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const getToday = () => new Date().toISOString().split("T")[0];

const getDayCellStyle = (value, maxInstallCount) => {
    const count = Number(value) || 0;

    if (!count || !maxInstallCount) {
        return {
            display: "block",
            textAlign: "center",
        };
    }

    const intensity = count / maxInstallCount;

    return {
        display: "block",
        textAlign: "center",
        fontWeight: 600,
        color: intensity > 0.55 ? "#ffffff" : "#1d4ed8",
        backgroundColor: "rgba(71, 110, 248, " + (0.1 + intensity * 0.7) + ")",
    };
};

function MobileAppInstalledPage() {
    const { showToast } = useContext(LogicContext);
    const [installations, setInstallations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [fromDate, setFromDate] = useState(getToday);
    const [toDate, setToDate] = useState(getToday);

    const normalizeRecords = (data) => (Array.isArray(data) ? data : []);

    const loadInstallations = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage("");

        try {
            const response = await mobileAppService.mobileInstalledList();
            setInstallations(normalizeRecords(response?.data?.app_install_info));
        } catch (error) {
            console.error("Unable to load mobile app installations", error);
            setInstallations([]);
            setErrorMessage("Unable to load mobile app installation data.");
        } finally {
            setIsLoading(false);
        }
    }, []);

    const searchByDate = async () => {
        if (!fromDate || !toDate) {
            showToast?.("Please select both dates", "error");
            return;
        }

        if (fromDate > toDate) {
            showToast?.("From Date cannot be after To Date", "error");
            return;
        }

        setIsLoading(true);
        setErrorMessage("");

        try {
            const response = await mobileAppService.mobileInstallDateRange({
                from_date: fromDate,
                to_date: toDate,
            });
            const records = normalizeRecords(response?.data);

            setInstallations(records);
            if (!records.length) {
                showToast?.("No installations found for the selected dates", "");
            }
        } catch (error) {
            console.error("Unable to search mobile app installations by date", error);
            setInstallations([]);
            setErrorMessage("Unable to load mobile app installation data.");
            showToast?.("Unable to search mobile app installations", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const matrixRows = useMemo(() => {
        const rowsByMonth = new Map();

        installations.forEach((record) => {
            const monthName = record?.month_name;
            const day = Number(record?.day_of_th_month);
            const installCount = Number(record?.number_of_install);
            const count = Number.isFinite(installCount) ? installCount : 0;

            if (!monthName || !Number.isInteger(day) || day < 1 || day > 31) {
                return;
            }

            if (!rowsByMonth.has(monthName)) {
                const row = {
                    key: monthName,
                    month_name: monthName,
                    total: 0,
                };

                DAYS_OF_MONTH.forEach((dayNumber) => {
                    row["day_" + dayNumber] = 0;
                });

                rowsByMonth.set(monthName, row);
            }

            const row = rowsByMonth.get(monthName);
            row["day_" + day] += count;
            row.total += count;
        });

        return Array.from(rowsByMonth.values()).sort((a, b) => {
            const firstMonth = MONTH_ORDER.indexOf(a.month_name);
            const secondMonth = MONTH_ORDER.indexOf(b.month_name);

            return (firstMonth < 0 ? MONTH_ORDER.length : firstMonth) -
                (secondMonth < 0 ? MONTH_ORDER.length : secondMonth);
        });
    }, [installations]);

    const dailyTotals = useMemo(
        () =>
            DAYS_OF_MONTH.map((day) =>
                matrixRows.reduce((total, row) => total + (row["day_" + day] || 0), 0)
            ),
        [matrixRows]
    );

    const grandTotal = useMemo(
        () => dailyTotals.reduce((total, value) => total + value, 0),
        [dailyTotals]
    );

    const maxInstallCount = useMemo(
        () =>
            matrixRows.reduce(
                (maximum, row) =>
                    Math.max(
                        maximum,
                        ...DAYS_OF_MONTH.map((day) => row["day_" + day] || 0)
                    ),
                0
            ),
        [matrixRows]
    );

    const tableColumns = useMemo(
        () => [
            {
                title: "Month",
                key: "month_name",
                dataIndex: "month_name",
                fixed: "left",
                sorter: (a, b) =>
                    MONTH_ORDER.indexOf(a?.month_name) - MONTH_ORDER.indexOf(b?.month_name),
            },
            {
                title: "Days of the Month",
                key: "days_of_month",
                align: "center",
                children: DAYS_OF_MONTH.map((day) => ({
                    title: day,
                    key: "day_" + day,
                    dataIndex: "day_" + day,
                    align: "center",
                    render: (value) => (
                        <span style={getDayCellStyle(value, maxInstallCount)}>
                            {value ?? 0}
                        </span>
                    ),
                })),
            },
            {
                title: "Total (Full Month)",
                key: "total",
                dataIndex: "total",
                align: "center",
                sorter: (a, b) => (a?.total ?? 0) - (b?.total ?? 0),
                render: (value) => <strong>{value ?? 0}</strong>,
            },
        ],
        [maxInstallCount]
    );

    const tableSummary = useCallback(
        () => (
            <Table.Summary>
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                        <strong>Total (Per Day)</strong>
                    </Table.Summary.Cell>
                    {dailyTotals.map((total, index) => (
                        <Table.Summary.Cell key={index} index={index + 1} align="center">
                            <strong>{total}</strong>
                        </Table.Summary.Cell>
                    ))}
                    <Table.Summary.Cell index={32} align="center">
                        <strong>{grandTotal}</strong>
                    </Table.Summary.Cell>
                </Table.Summary.Row>
            </Table.Summary>
        ),
        [dailyTotals, grandTotal]
    );

    useEffect(() => {
        document.title = "Mobile App Installed";
        loadInstallations();
    }, [loadInstallations]);

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Monthly Install Users</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Daily wise install user count for each month.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={loadInstallations}
                    disabled={isLoading}
                    title="Refresh mobile installation data"
                    aria-label="Refresh mobile installation data"
                    className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    <IoMdRefresh className="h-5 w-5" />
                    <span>{isLoading ? "Loading..." : "Refresh"}</span>
                </button>
            </div>

            <div className="mb-6 rounded-lg bg-white p-4 shadow-md">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                    <div>
                        <label
                            htmlFor="mobile-install-from-date"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            From Date
                        </label>
                        <input
                            id="mobile-install-from-date"
                            type="date"
                            value={fromDate}
                            onChange={(event) => setFromDate(event.target.value)}
                            className="rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="mobile-install-to-date"
                            className="mb-1 block text-sm font-medium text-gray-700"
                        >
                            To Date
                        </label>
                        <input
                            id="mobile-install-to-date"
                            type="date"
                            value={toDate}
                            onChange={(event) => setToDate(event.target.value)}
                            className="rounded-md border border-gray-300 px-4 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={searchByDate}
                        disabled={isLoading}
                        className="rounded-md bg-blue-600 px-4 py-2 text-white shadow-md transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        Search by Date
                    </button>
                </div>
            </div>

            {errorMessage && <p className="mb-4 text-sm text-red-600">{errorMessage}</p>}
            {isLoading && matrixRows.length === 0 && (
                <p className="mb-4 text-sm text-gray-500">Loading installation data...</p>
            )}

            <GeneralCustomTable
                tableHeadList={tableColumns}
                tableDataList={matrixRows}
                allowCheckBox={false}
                tableSummary={matrixRows.length ? tableSummary : undefined}
                rowKey="key"
            />
        </div>
    );
}

export default MobileAppInstalledPage;
